import { EventEmitter } from "./EventEmitter.js";
import type { ControlEvents, DragEventArgs, DragPayload } from "./events.js";
import type { Alignment, Insets, Point, Rect, Size } from "../../common/geometry.js";
import { rectContains, rectIntersects } from "../../common/geometry.js";
import { pointInShape, silhouetteOf } from "../art/markup/shapes.js";
import type { RenderContext, TooltipContent } from "../render/RenderContext.js";
import {
  describeByShape,
  describerFor,
  itemSourceFor,
  type AccessibleItems,
  type AccessibleNode,
} from "./accessibility.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import type { Container } from "./Container.js";
import type { Desktop } from "./Desktop.js";
import { FlareList } from "../flare/FlareList.js";
import { MotionList } from "../motion/MotionList.js";
import type { MotionValues } from "../motion/animation.js";

/** WinForms-style edge anchoring, resolved when the parent resizes. */
export interface Anchor {
  left: boolean;
  top: boolean;
  right: boolean;
  bottom: boolean;
}

export const ANCHOR_TOP_LEFT: Anchor = { left: true, top: true, right: false, bottom: false };

/**
 * WinForms-style docking: the parent hands this control a whole edge of its
 * client area, or the space left in the middle.
 *
 * Docking beats anchoring for chrome. Anchors say "keep this many pixels
 * between me and that edge", which the application has to get right once by
 * hand and which drifts if the control is ever squeezed flat; docking says
 * "you own the bottom", and the parent recomputes every child's rectangle
 * from the current client area on every layout pass. A window whose menu bar
 * docks top and status bar docks bottom cannot end up with its content
 * painted over either one, at any size, in any order.
 *
 * `none` is the default, so a control laid out with `x`/`y` and anchors is
 * untouched.
 */
export type Dock = "none" | "left" | "top" | "right" | "bottom" | "fill";

const NO_MARGIN: Insets = { left: 0, top: 0, right: 0, bottom: 0 };

export interface ControlOptions {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  /** Let the parent size this axis from `preferredSize` instead of `width`. */
  autoWidth?: boolean;
  /** Let the parent size this axis from `preferredSize` instead of `height`. */
  autoHeight?: boolean;
  /** The smallest size any layout, or the application, may give this control. */
  minimumSize?: Size | null;
  /** The largest size any layout, or the application, may give this control. */
  maximumSize?: Size | null;
  visible?: boolean;
  enabled?: boolean;
  tabStop?: boolean;
  tabIndex?: number;
  anchor?: Partial<Anchor>;
  /** Place this control on the horizontal axis by alignment instead of by `x`. */
  hAlign?: Alignment | null;
  /** Place this control on the vertical axis by alignment instead of by `y`. */
  vAlign?: Alignment | null;
  /** Give this control an edge of the parent's client area, or the middle. */
  dock?: Dock;
  /** Space held clear around this control while docked. */
  margin?: Insets;
  cursor?: string;
  name?: string;
  /** Hover text. Every control has one; a glyph button usually needs one. */
  tooltip?: TooltipContent;
  /** What a screen reader calls this, overriding what it would say for itself. */
  accessibleName?: string | null;
}

let nextControlId = 1;

/**
 * Base class for everything drawable and interactive.
 *
 * Coordinates are logical pixels relative to the parent's client area. During
 * painting the RenderContext transform is set so the control's own top-left is
 * the origin, so `onPaint` always draws in the rectangle (0, 0, width, height).
 */
export abstract class Control extends EventEmitter<ControlEvents> {
  #flare: FlareList | null = null;
  #motion: MotionList | null = null;
  readonly id = nextControlId++;
  name = "";

  #x = 0;
  #y = 0;
  #width = 100;
  #height = 24;
  #autoWidth = false;
  #autoHeight = false;
  #minimumSize: Size | null = null;
  #maximumSize: Size | null = null;
  #visible = true;
  #enabled = true;
  #hovered = false;
  #pressed = false;

  parent: Container | null = null;
  anchor: Anchor = { ...ANCHOR_TOP_LEFT };
  #dock: Dock = "none";
  #hAlign: Alignment | null = null;
  #vAlign: Alignment | null = null;
  #margin: Insets = NO_MARGIN;
  tabStop = false;
  tabIndex = 0;
  /** CSS cursor name applied while the pointer is over this control. */
  cursor = "default";
  /** Arbitrary payload for application code, like WinForms' Control.Tag. */
  tag: unknown = undefined;

  constructor(options: ControlOptions = {}) {
    super();
    if (options.x !== undefined) this.#x = options.x;
    if (options.y !== undefined) this.#y = options.y;
    if (options.width !== undefined) this.#width = options.width;
    if (options.height !== undefined) this.#height = options.height;
    if (options.autoWidth !== undefined) this.#autoWidth = options.autoWidth;
    if (options.autoHeight !== undefined) this.#autoHeight = options.autoHeight;
    if (options.minimumSize) this.#minimumSize = options.minimumSize;
    if (options.maximumSize) this.#maximumSize = options.maximumSize;
    if (this.#minimumSize || this.#maximumSize) {
      const c = this.constrainSize(this.#width, this.#height);
      this.#width = c.width;
      this.#height = c.height;
    }
    if (options.visible !== undefined) this.#visible = options.visible;
    if (options.enabled !== undefined) this.#enabled = options.enabled;
    if (options.tabStop !== undefined) this.tabStop = options.tabStop;
    if (options.tabIndex !== undefined) this.tabIndex = options.tabIndex;
    if (options.anchor) Object.assign(this.anchor, options.anchor);
    if (options.dock !== undefined) this.#dock = options.dock;
    if (options.hAlign !== undefined) this.#hAlign = options.hAlign;
    if (options.vAlign !== undefined) this.#vAlign = options.vAlign;
    if (options.margin !== undefined) this.#margin = options.margin;
    if (options.cursor !== undefined) this.cursor = options.cursor;
    if (options.name !== undefined) this.name = options.name;
    if (options.tooltip !== undefined) this.tooltip = options.tooltip;
    if (options.accessibleName !== undefined) this.accessibleName = options.accessibleName;
  }

  // --- geometry ---------------------------------------------------------

  get x(): number {
    return this.#x;
  }
  set x(v: number) {
    this.setBounds(v, this.#y, this.#width, this.#height);
  }

  get y(): number {
    return this.#y;
  }
  set y(v: number) {
    this.setBounds(this.#x, v, this.#width, this.#height);
  }

  get width(): number {
    return this.#width;
  }
  set width(v: number) {
    this.setBounds(this.#x, this.#y, v, this.#height);
  }

  get height(): number {
    return this.#height;
  }
  set height(v: number) {
    this.setBounds(this.#x, this.#y, this.#width, v);
  }

  get bounds(): Rect {
    return { x: this.#x, y: this.#y, width: this.#width, height: this.#height };
  }
  set bounds(r: Rect) {
    this.setBounds(r.x, r.y, r.width, r.height);
  }

  /**
   * Which edge of the parent's client area this control takes, if any.
   *
   * Docked controls are laid out by the parent on every pass and ignore
   * `anchor` — the two are alternatives, not a pair. Changing this relays out
   * the parent immediately, so a status bar can be hidden and shown and the
   * content above it takes back the space both ways.
   */
  get dock(): Dock {
    return this.#dock;
  }
  set dock(v: Dock) {
    if (this.#dock === v) return;
    this.#dock = v;
    this.parent?.layoutDocked();
    this.invalidate();
  }

  /**
   * Place this control horizontally by alignment rather than by `x`.
   *
   * Null - the default - means the control keeps the `x` it was given, which is the WinForms
   * behavior every existing layout depends on. Set it and the parent positions the control on
   * every pass, `margin` becoming the gap it holds to the edges; `stretch` makes it fill.
   *
   * The three placement models are alternatives, in order of precedence: `dock` wins, then
   * alignment, then the control's own coordinates with `anchor` holding them on a resize. A
   * `LayoutPanel` ignores all of this and arranges its children itself.
   */
  get hAlign(): Alignment | null {
    return this.#hAlign;
  }
  set hAlign(v: Alignment | null) {
    if (this.#hAlign === v) return;
    this.#hAlign = v;
    this.parent?.layout();
  }

  /** Place this control vertically by alignment rather than by `y`. See `hAlign`. */
  get vAlign(): Alignment | null {
    return this.#vAlign;
  }
  set vAlign(v: Alignment | null) {
    if (this.#vAlign === v) return;
    this.#vAlign = v;
    this.parent?.layout();
  }

  /**
   * Space held clear around this control while it is docked, or aligned.
   *
   * Docking carves it out of the parent's remaining space, so two bars docked to the same edge
   * stack with the gap between them. Alignment holds it to whichever edges the alignment
   * measures from. A control placed by its own `x`/`y` ignores it: the gap is already in the
   * coordinates.
   */
  get margin(): Insets {
    return this.#margin;
  }
  set margin(v: Insets) {
    this.#margin = v;
    if (this.#dock !== "none") this.parent?.layoutDocked();
    else if (this.#hAlign !== null || this.#vAlign !== null) this.parent?.layout();
  }

  /** The control's own rectangle with the origin at its top-left. */
  get localRect(): Rect {
    return { x: 0, y: 0, width: this.#width, height: this.#height };
  }

  get size(): Size {
    return { width: this.#width, height: this.#height };
  }

  /**
   * Let the parent set this axis from `preferredSize` rather than from `width`.
   *
   * Off by default, so a control sized by the application keeps the size it was given and every
   * layout written against the imperative API behaves exactly as it did. Turned on, the control
   * stops carrying a width of its own: docking uses the preferred thickness for a bar, alignment
   * uses the preferred extent for a non-stretched child, and a layout panel measures instead of
   * reading back the size it last wrote.
   */
  get autoWidth(): boolean {
    return this.#autoWidth;
  }
  set autoWidth(v: boolean) {
    if (this.#autoWidth === v) return;
    this.#autoWidth = v;
    this.parent?.onChildMeasureChanged(this);
  }

  /** Let the parent set this axis from `preferredSize` rather than from `height`. See `autoWidth`. */
  get autoHeight(): boolean {
    return this.#autoHeight;
  }
  set autoHeight(v: boolean) {
    if (this.#autoHeight === v) return;
    this.#autoHeight = v;
    this.parent?.onChildMeasureChanged(this);
  }

  /**
   * WinForms' `MinimumSize`: every `setBounds` is held to it, whoever calls it — docking,
   * anchoring, a layout panel, a window drag or the application. Null is no limit.
   */
  get minimumSize(): Size | null {
    return this.#minimumSize;
  }
  set minimumSize(v: Size | null) {
    this.#minimumSize = v;
    this.#applySizeLimits();
  }

  /** WinForms' `MaximumSize`. A maximized window stops at it, at the work area's top-left. */
  get maximumSize(): Size | null {
    return this.#maximumSize;
  }
  set maximumSize(v: Size | null) {
    this.#maximumSize = v;
    this.#applySizeLimits();
  }

  /** `width` and `height` held to `minimumSize` and `maximumSize`; the minimum wins a conflict. */
  constrainSize(width: number, height: number): Size {
    const min = this.#minimumSize;
    const max = this.#maximumSize;
    if (max) {
      if (max.width > 0) width = Math.min(width, max.width);
      if (max.height > 0) height = Math.min(height, max.height);
    }
    if (min) {
      width = Math.max(width, min.width);
      height = Math.max(height, min.height);
    }
    return { width, height };
  }

  #applySizeLimits(): void {
    this.setBounds(this.#x, this.#y, this.#width, this.#height);
    this.parent?.onChildMeasureChanged(this);
  }

  /**
   * The size this control would like, given the space the parent can offer it.
   *
   * The default is the size it already has, which is what makes the whole mechanism opt-in: a
   * control that has not overridden this is measured as exactly what the application made it.
   * Overrides measure their content — text, glyph, theme metrics — none of which is available
   * until the control is attached, so every one of them returns the current size when `desktop`
   * is null and is re-run from `onEnvironmentChanged` once it is not.
   *
   * `available` is in the parent's child-space units. It matters for anything that reflows, a
   * wrapping label above all; a control that does not care ignores it.
   */
  preferredSize(_available: Size): Size {
    return this.size;
  }

  /**
   * Say that `preferredSize` may have changed — new text, a new font, different items.
   *
   * Only a control the parent is actually measuring needs to relay out, so this is a no-op
   * unless an axis is auto.
   */
  protected invalidateMeasure(): void {
    if (this.#autoWidth || this.#autoHeight) this.parent?.onChildMeasureChanged(this);
  }

  setBounds(x: number, y: number, width: number, height: number): void {
    if (this.#minimumSize || this.#maximumSize) ({ width, height } = this.constrainSize(width, height));
    width = Math.max(0, width);
    height = Math.max(0, height);
    const moved = x !== this.#x || y !== this.#y;
    const resized = width !== this.#width || height !== this.#height;
    if (!moved && !resized) return;
    this.#x = x;
    this.#y = y;
    this.#width = width;
    this.#height = height;
    if (resized) {
      this.onResize();
      this.emit("resize");
      // A docked bar that changes its own thickness — a status bar growing to
      // fit a taller font — moves everything docked after it, so the parent
      // has to carve the edges again. Skipped while the parent is mid-pass,
      // which is where most of these calls come from.
      const parent = this.parent;
      if (this.#dock !== "none" && parent && !parent.layingOutDocked) parent.layoutDocked();
    }
    if (moved) this.emit("move");
    this.invalidate();
  }

  setLocation(x: number, y: number): void {
    this.setBounds(x, y, this.#width, this.#height);
  }

  setSize(width: number, height: number): void {
    this.setBounds(this.#x, this.#y, width, height);
  }

  /** Called after the control's own size changes; containers re-layout here. */
  protected onResize(): void {}

  /**
   * Called when the control gains a Desktop, or when the theme changes.
   *
   * Until a control is attached it has no theme metrics and cannot measure
   * text, so anything that depends on either — scroll bar ranges, auto-sizing,
   * cached content widths — has to be (re)computed here rather than in the
   * constructor.
   */
  protected onEnvironmentChanged(): void {}

  /** Framework-internal: run the environment hook on this control's subtree. */
  notifyEnvironmentChanged(): void {
    // Gaining or losing a desktop changes where an animated flare registers.
    this.#flare?.refresh();
    this.#motion?.refresh();
    this.onEnvironmentChanged();
  }

  // --- state ------------------------------------------------------------

  get visible(): boolean {
    return this.#visible;
  }
  set visible(v: boolean) {
    if (this.#visible === v) return;
    this.#visible = v;
    if (!v) {
      this.#hovered = false;
      this.#pressed = false;
      // Focus anywhere inside an invisible subtree is unreachable: the user
      // cannot see the caret and cannot click their way back out.
      const focus = this.desktop?.focusManager;
      if (focus && this.contains(focus.focused)) focus.clear();
    }
    this.emit("visiblechanged");
    // A hidden docked control gives its edge back, and takes it again when it
    // returns; without this the space it held would stay reserved and empty.
    if (this.#dock !== "none") this.parent?.layoutDocked();
    this.parent?.invalidate();
    this.invalidate();
  }

  get enabled(): boolean {
    return this.#enabled;
  }
  set enabled(v: boolean) {
    if (this.#enabled === v) return;
    this.#enabled = v;
    if (!v) {
      this.#hovered = false;
      this.#pressed = false;
      if (this.focused) this.desktop?.focusManager.moveNext();
    }
    this.emit("enabledchanged");
    this.invalidate();
  }

  /** False when this control or any ancestor is hidden. */
  get effectivelyVisible(): boolean {
    let c: Control | null = this;
    while (c) {
      if (!c.#visible) return false;
      c = c.parent;
    }
    return true;
  }

  /** False when this control or any ancestor is disabled. */
  get effectivelyEnabled(): boolean {
    let c: Control | null = this;
    while (c) {
      if (!c.#enabled) return false;
      c = c.parent;
    }
    return true;
  }

  /**
   * Set by a control that covers its own content with something the user must
   * wait out — today only a busy container. Everything underneath stays
   * visible and enabled, so no application state is disturbed, but the
   * pointer and Tab cannot reach it.
   */
  protected blocksInput = false;

  /**
   * The region, in this control's own space, that it is currently holding
   * input off — a busy container's scrim. Null when it is holding nothing
   * off, which is every control but a busy `Container`.
   */
  get inputBlockRect(): Rect | null {
    return null;
  }

  /** True when this control or any ancestor is holding input off. */
  get inputBlocked(): boolean {
    let c: Control | null = this;
    while (c) {
      if (c.blocksInput) return true;
      c = c.parent;
    }
    return false;
  }

  get hovered(): boolean {
    return this.#hovered;
  }
  get pressed(): boolean {
    return this.#pressed;
  }

  /** Framework-internal: the input manager owns hover and press state. */
  setHovered(v: boolean): void {
    if (this.#hovered === v) return;
    this.#hovered = v;
    this.invalidate();
  }

  setPressed(v: boolean): void {
    if (this.#pressed === v) return;
    this.#pressed = v;
    this.invalidate();
  }

  get focused(): boolean {
    return this.desktop?.focusManager.focused === this;
  }

  focus(): boolean {
    return this.desktop?.focusManager.set(this) ?? false;
  }

  /** Can this control take keyboard focus right now? */
  get focusable(): boolean {
    return this.tabStop && this.effectivelyVisible && this.effectivelyEnabled && !this.inputBlocked;
  }

  /**
   * True for controls that consume printable keystrokes.
   *
   * The Desktop suppresses page-scrolling keys (space, arrows, Backspace) so
   * they do not scroll the host document. While one of these controls has
   * focus that suppression must not apply to printable keys, or the browser
   * never inserts the character into the hidden field and no `input` event is
   * ever produced — space in particular becomes untypable.
   */
  get acceptsTextInput(): boolean {
    return false;
  }

  /**
   * True when a drag starting at `p` belongs to this control.
   *
   * On touch, one finger dragging scrolls whatever is under it — that is what
   * a phone does, and it is worth more than drag-selecting a list. Controls
   * whose whole purpose is the drag say so here and keep it: a scroll bar
   * thumb, a splitter, a window's title bar, a column divider.
   */
  handlesDragAt(_p: Point): boolean {
    return false;
  }

  /**
   * Scroll by a delta in content pixels, and report how much was used.
   *
   * The sign matches the wheel: a positive `dy` moves the view down the
   * content. Anything left over is handed to the parent, so a list inside a
   * scroll viewer hands the overshoot outward once it hits its end — the same
   * chaining a browser does.
   *
   * Only scrolling controls implement it; everything else consumes nothing.
   */
  panBy(_dx: number, _dy: number): Point {
    return { x: 0, y: 0 };
  }

  /**
   * The pan is over. Return true to claim the release and stop the throw.
   *
   * Offered up the same chain `panBy` uses, so a control that took the
   * movement gets to decide what letting go means. Scrolling controls want
   * nothing here — coasting to a stop is the right ending and the desktop
   * does it for them. A control that snaps, as a carousel lands on a page,
   * claims the release instead: it is finishing the gesture itself, and a
   * fling on top of that would fight the slide.
   *
   * `vx`/`vy` are the throw in content pixels per second, in the same sense
   * as `panBy`.
   */
  panEnd(_vx: number, _vy: number): boolean {
    return false;
  }

  /**
   * True when dragging with a mouse should pan this control, as a finger does.
   *
   * Off by default, and deliberately: a mouse drag over a list selects rows
   * and a drag over text selects text, so making every scrollable pan under
   * the mouse would take those away. A control turns it on when the drag is
   * the point of it — a carousel is swiped whether or not there is a
   * touchscreen — and the press that started it is abandoned, exactly as the
   * touch claim abandons one.
   */
  get pansWithMouse(): boolean {
    return false;
  }

  /**
   * Scale about `at`, a point in this control's own coordinates, by a
   * multiplier — 1.1 to grow a tenth, 0.9 to shrink.
   *
   * Returns whether anything happened, so a pinch that has run into a zoom
   * limit chains to an outer viewport instead of stopping dead, the same way
   * `panBy` chains. Only `ScrollViewer` implements it.
   */
  zoomBy(_factor: number, _at: Point): boolean {
    return false;
  }

  visualState(): VisualState {
    return {
      hovered: this.#hovered,
      pressed: this.#pressed,
      focused: this.focused,
      disabled: !this.effectivelyEnabled,
    };
  }

  // --- tree -------------------------------------------------------------

  /** The Desktop this control belongs to, or null when detached. */
  get desktop(): Desktop | null {
    let c: Control | null = this;
    while (c.parent) c = c.parent;
    return (c as { asDesktop?: Desktop }).asDesktop ?? null;
  }

  /** Nearest ancestor Window, or null. */
  findAncestor<T extends Control>(predicate: (c: Control) => c is T): T | null {
    let c: Container | null = this.parent;
    while (c) {
      if (predicate(c)) return c;
      c = c.parent;
    }
    return null;
  }

  contains(other: Control | null): boolean {
    let c: Control | null = other;
    while (c) {
      if (c === this) return true;
      c = c.parent;
    }
    return false;
  }

  // --- coordinate mapping -----------------------------------------------

  /** Map a point in this control's space into its parent's space. */
  toParentSpace(p: Point): Point {
    const q = { x: p.x + this.#x, y: p.y + this.#y };
    return this.parent ? this.parent.childToLocal(q) : q;
  }

  fromParentSpace(p: Point): Point {
    const q = this.parent ? this.parent.localToChild(p) : p;
    return { x: q.x - this.#x, y: q.y - this.#y };
  }

  /** Map a point in this control's space to canvas (screen) coordinates. */
  toScreen(p: Point): Point {
    let q = p;
    let c: Control | null = this;
    while (c) {
      q = c.toParentSpace(q);
      c = c.parent;
    }
    return q;
  }

  /** Map a canvas point into this control's space. */
  fromScreen(p: Point): Point {
    const chain: Control[] = [];
    let c: Control | null = this;
    while (c) {
      chain.push(c);
      c = c.parent;
    }
    let q = p;
    for (let i = chain.length - 1; i >= 0; i--) q = chain[i]!.fromParentSpace(q);
    return q;
  }

  /** This control's rectangle in canvas coordinates. */
  get screenBounds(): Rect {
    const tl = this.toScreen({ x: 0, y: 0 });
    const br = this.toScreen({ x: this.#width, y: this.#height });
    return { x: tl.x, y: tl.y, width: br.x - tl.x, height: br.y - tl.y };
  }

  /** Cumulative scale from this control's space to canvas space. */
  get screenScale(): number {
    let scale = 1;
    let c: Container | null = this.parent;
    while (c) {
      scale *= c.childScale;
      c = c.parent;
    }
    return scale;
  }

  // --- hit testing ------------------------------------------------------

  /**
   * Find the deepest control at `p`, expressed in this control's own space.
   * Returns null when the point misses.
   */
  hitTest(p: Point): Control | null {
    if (!this.#visible) return null;
    if (!rectContains(this.localRect, p)) return null;
    if (!this.insideShape(p)) return null;
    return this.hitTestSelf(p) ? this : null;
  }

  // --- shape --------------------------------------------------------------

  #shape: string | null = null;
  #shapeProvider: ((width: number, height: number) => Point[][]) | null = null;
  #silhouette: { w: number; h: number; rings: Point[][] } | null = null;

  /**
   * The control's outline, when it is not its rectangle: a preset - `circle`, `pill`, `hexagon`,
   * `octagon`, `diamond`, `chevron`, `tab` - or SVG path data in a 100 by 100 box stretched over
   * the control. The silhouette is what the control paints as its chrome, what a click must land
   * inside, what the focus ring follows and what a glow spreads from. Its children are still clipped
   * to the rectangle; keep them inside with padding.
   */
  get shape(): string | null {
    return this.#shape;
  }
  set shape(v: string | null) {
    this.#shape = v && v.trim() ? v : null;
    this.#silhouette = null;
    this.invalidate();
  }

  /** An outline worked out from the size rather than written as a string - a markup `<Shape>`. */
  get shapeProvider(): ((width: number, height: number) => Point[][]) | null {
    return this.#shapeProvider;
  }
  set shapeProvider(v: ((width: number, height: number) => Point[][]) | null) {
    this.#shapeProvider = v;
    this.#silhouette = null;
    this.invalidate();
  }

  /** The outline in local pixels, or null for a plain rectangle. Cached until the size changes. */
  shapeOutline(): Point[][] | null {
    if (!this.#shape && !this.#shapeProvider) return null;
    const w = this.#width;
    const h = this.#height;
    const kept = this.#silhouette;
    if (kept && kept.w === w && kept.h === h) return kept.rings;
    let rings: Point[][] = [];
    try {
      rings = this.#shapeProvider ? this.#shapeProvider(w, h) : silhouetteOf(this.#shape!, w, h);
    } catch {
      rings = [];
    }
    this.#silhouette = { w, h, rings };
    return rings;
  }

  /** False when the control has a shape and `p` is outside it; corners of a round button are not the button. */
  protected insideShape(p: Point): boolean {
    const rings = this.shapeOutline();
    return !rings || rings.length === 0 || pointInShape(p, rings);
  }

  /**
   * Whether the point counts as hitting this control's own surface. Override
   * for non-rectangular controls or to punch holes through a container.
   */
  protected hitTestSelf(_p: Point): boolean {
    return true;
  }

  /**
   * Cursor to show at a point in this control's space. Override when the
   * cursor varies by position, as it does over a window's resize grips.
   */
  cursorAt(_p: Point): string {
    return this.cursor;
  }

  // --- painting ---------------------------------------------------------

  /** Request a repaint of the whole desktop on the next frame. */
  invalidate(): void {
    this.desktop?.invalidate();
  }

  /**
   * Framework entry point. The RenderContext transform already places this
   * control's top-left at the origin.
   */
  /**
   * Decorative effects layered on this control: outlines, shadows, glows.
   *
   * Created on first use, so a control that wears none costs nothing.
   */
  get flare(): FlareList {
    return (this.#flare ??= new FlareList(this));
  }

  /** True when effects are attached, without creating the list to find out. */
  get hasFlare(): boolean {
    return this.#flare !== null && this.#flare.length > 0;
  }

  /**
   * Animation groups on this control: entrances, loops, hover and press reactions.
   *
   * Created on first use, like `flare`. The parent applies what they come to around this
   * control's whole paint, so nothing in `onPaint` has to know it is moving.
   */
  get motion(): MotionList {
    return (this.#motion ??= new MotionList(this));
  }

  /** True when groups are attached, without creating the list to find out. */
  get hasMotion(): boolean {
    return this.#motion !== null && this.#motion.length > 0;
  }

  /**
   * How much of itself to draw, 0..1: what a `drawIn` entrance has reached. Always 1 for a
   * control with no motion.
   *
   * Read by controls that can draw partially - a ring that sweeps round, a rule that grows from
   * its center. Anything else ignores it and is simply faded or moved by its other channels.
   */
  get drawProgress(): number {
    return this.#motion?.values.reveal ?? 1;
  }

  /**
   * Advance this control's motion for the frame being painted. Null when it has none.
   *
   * @internal The parent calls this while painting its children.
   */
  motionFrame(onScreen: boolean): MotionValues | null {
    const motion = this.#motion;
    if (!motion || motion.length === 0) return null;
    const desktop = this.desktop;
    return motion.frame(
      desktop?.frameTime ?? 0,
      onScreen,
      this.visualState(),
      desktop?.reducedMotion ?? false,
    );
  }

  paint(rc: RenderContext, theme: Theme): void {
    if (!this.isPaintable(rc)) return;
    this.withFlare(rc, theme, () => this.onPaint(rc, theme));
  }

  /**
   * Run `body` between the flare's below and above passes.
   *
   * Containers paint their children too, so they call this around the whole
   * subtree rather than just their own background — which is what makes a
   * glow behind a window, or sparkles over a panel's contents, work.
   */
  protected withFlare(rc: RenderContext, theme: Theme, body: () => void): void {
    const flare = this.#flare;
    if (!flare || flare.length === 0) {
      body();
      return;
    }
    const now = this.desktop?.frameTime ?? 0;
    const ctx = flare.frame(
      { rc, theme, control: this, bounds: this.localRect, state: this.visualState() },
      now,
    );
    if (!ctx) {
      body();
      return;
    }
    flare.paintBelow(ctx, now);
    body();
    flare.paintAbove(ctx, now);
  }

  /** Visibility + clip culling shared by Control.paint and Container.paint. */
  /**
   * Hover text. Null, or empty, means no tooltip.
   *
   * A plain string is the common case; an array of `TextSpan` gets a styled
   * tip — a rarity-colored item name over its stat lines, say — wrapped by
   * the same rule plain text uses.
   */
  tooltip: TooltipContent | null = null;

  /**
   * The tooltip for a given point in this control, in local coordinates.
   *
   * Defaults to the whole-control `tooltip`. Controls that draw several
   * things in one rectangle — a toolbar, a list — override it so each part
   * can name itself.
   */
  getTooltip(_p: Point): TooltipContent | null {
    return this.tooltip;
  }

  /**
   * What a screen reader calls this control, overriding whatever it would say
   * for itself.
   *
   * Null - the default - means "work it out", which is right for a Button,
   * whose caption is its name, and wrong for a glyph button, whose caption is
   * a triangle.
   */
  accessibleName: string | null = null;

  /**
   * How this control describes itself to the accessibility mirror.
   *
   * Overriding this is how a control nobody has registered takes part. The
   * default asks the describer table - which is empty until an application
   * turns accessibility on - and otherwise reads whatever shape the control
   * already has, which is enough for most of the library.
   *
   * Returning null means "contribute no element": right for a layout panel,
   * and the reason a reader does not walk through three groups to reach a
   * button.
   */
  describeAccessible(): AccessibleNode | null {
    const node = describerFor(this.constructor)?.(this) ?? describeByShape(this);
    // Applied here rather than in every describer: one rule, one place, and
    // no describer that quietly forgets to honor it.
    if (this.accessibleName === null) return node;
    // Naming something is also how an application says "expose this". A
    // viewport, a custom control and a panel that is really a landmark all
    // describe as nothing on their own, and a name that was set and then
    // ignored is the most confusing answer available.
    if (!node) return { role: "group", name: this.accessibleName };
    return { ...node, name: this.accessibleName };
  }

  /**
   * The rows this control draws itself, if it draws any.
   *
   * Null for almost everything. A list, a grid, a tree or a menu holds its
   * contents as data and paints only what is on screen, and this is how the
   * mirror gets at them without the control having to hand over a child per
   * row it never built.
   */
  accessibleItems(): AccessibleItems | null {
    return itemSourceFor(this.constructor)?.(this) ?? null;
  }

  // --- drag and drop ------------------------------------------------------

  /**
   * True when this control would take `payload` released at `p` (local).
   *
   * False by default, so nothing is a drop target until it says it is. The
   * session asks this on every pointer move, and asks this control's ancestors
   * when it says no — an item slot with no room can decline while the grid
   * behind it still accepts.
   */
  canAcceptDrop(_payload: DragPayload, _p: Point): boolean {
    return false;
  }

  /**
   * Take a dropped payload. Return false to refuse it after all — a slot that
   * accepted the hover can still find, on release, that it has no room.
   *
   * Overriding this is the normal way to receive a drop; the `drop` event is
   * for a caller wiring one up from outside.
   */
  protected onDrop(_payload: DragPayload, _p: Point): boolean {
    return true;
  }

  /** Framework entry point: run `onDrop`, then raise the event. */
  handleDrop(args: DragEventArgs): void {
    if (!this.onDrop(args.payload, { x: args.x, y: args.y })) args.accepted = false;
    this.emit("drop", args);
  }

  /**
   * How far past its own bounds this control paints, in px.
   *
   * Flare is the usual reason — a glow reaches further than the button it
   * surrounds. Subclasses can widen it for chrome of their own.
   */
  get paintOverflow(): number {
    return this.#flare?.overflow ?? 0;
  }

  protected isPaintable(rc: RenderContext): boolean {
    if (!this.#visible || this.#width <= 0 || this.#height <= 0) return false;
    if (rc.isClippedOut) return false;
    // Cull against the painted area, not just the bounds: a control can be
    // off-screen while its shadow is still visible.
    const over = this.paintOverflow;
    const r = this.localRect;
    const painted =
      over > 0
        ? { x: r.x - over, y: r.y - over, width: r.width + over * 2, height: r.height + over * 2 }
        : r;
    return rectIntersects(rc.rectToScreen(painted), rc.clip);
  }

  /** Draw the control inside (0, 0, width, height). */
  protected abstract onPaint(rc: RenderContext, theme: Theme): void;
}
